import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classNames from 'classnames';
import { ExpandableContent } from '../../components/global/ExpandableContent/ExpandableContent';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import {
    iconicDesignsText,
    aboutTheDesignText,
    creatorNameText,
    readMore,
    readLess,
} from '../../shared/sharedMessages';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

import styles from './SbSharedContentModuleIconicDesign.scss';

const IMG_SIZES = '(max-width: 568px) 100vw, 50vw';

function SbSharedContentModuleIconicDesignComponent({ browseContentModule, designPageInfo }) {
    const { description, title, mainImage: image } = browseContentModule;
    if (!image || !title) {
        return null;
    }
    const creators = designPageInfo?.design?.creators || [];
    let firstCreatorAdded = false;
    return (
        <div className={styles.container} data-tn="iconic-designs-container">
            <img
                data-tn="designs-image"
                className={styles.image}
                src={image}
                alt={title}
                srcSet={getSrcsetString(image)}
                sizes={IMG_SIZES}
            />
            <div
                className={classNames(styles.titleContainer, {
                    [styles.titleContainerNoDescription]: !description,
                })}
            >
                <div className={styles.iconicDesignsText} data-tn="iconic-designs-text">
                    {iconicDesignsText}
                </div>
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.iconicDesignsHeader}>
                            <span
                                className={styles.iconicDesignTitle}
                                data-tn="iconic-designs-title"
                            >
                                {title}
                            </span>
                            {creators.map(creator => {
                                const {
                                    browsePageLinkData: creatorBrowsePageLinkData,
                                    displayName: creatorDisplayName,
                                    name: creatorName,
                                } = creator || {};
                                const creatorLinkText = creatorDisplayName || creatorName;
                                if (!creatorLinkText) {
                                    return null;
                                }

                                const creatorLink = (
                                    <SeoLink
                                        dataTn="creator-link"
                                        linkData={creatorBrowsePageLinkData || {}}
                                        className={styles.locationLink}
                                    >
                                        {creatorLinkText}
                                    </SeoLink>
                                );

                                let creatorText = creatorLink;
                                /* only add "By" on first creator */
                                if (!firstCreatorAdded) {
                                    creatorText = creatorNameText(creatorLink);
                                    firstCreatorAdded = true;
                                }
                                return (
                                    <span className={styles.creatorName} key={creatorName}>
                                        {creatorText}
                                    </span>
                                );
                            })}
                        </Heading>
                    )}
                </HeadingLevel>
            </div>
            {description && (
                <>
                    <div className={styles.divider} />
                    <div className={styles.aboutTheDesignText}>{aboutTheDesignText}</div>
                    <ExpandableContent
                        collapseHeight={192}
                        buttonClassName={styles.expandButton}
                        showReadMoreArrow={false}
                        readMoreMessage={readMore}
                        readLessMessage={readLess}
                    >
                        <span
                            className={styles.description}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </ExpandableContent>
                </>
            )}
            <div className={`${styles.divider} ${styles.hideOnResponsive}`} />
        </div>
    );
}

SbSharedContentModuleIconicDesignComponent.propTypes = {
    browseContentModule: PropTypes.object.isRequired,
    designPageInfo: PropTypes.object,
};

export const SbSharedContentModuleIconicDesign = createFragmentContainer(
    SbSharedContentModuleIconicDesignComponent,
    {
        browseContentModule: graphql`
            fragment SbSharedContentModuleIconicDesign_browseContentModule on ContentModuleIconicDesign {
                description
                title
                mainImage
            }
        `,
        designPageInfo: graphql`
            fragment SbSharedContentModuleIconicDesign_designPageInfo on DesignPageInfo {
                design {
                    creators {
                        displayName
                        name
                        browsePageLinkData {
                            ...SeoLink_linkData
                        }
                    }
                }
            }
        `,
    }
);
